import { useEffect, useMemo } from 'react';
import type { HedgeBookEntry } from '../api/hedge-book';
import { useOrderHedgingSummary } from '../api/order-summary';
import { FxCloseDialog } from './fx-close-dialog';

function isOpenFxCover(entry: HedgeBookEntry): boolean {
  if (entry.instrumentType !== 'FX') return false;
  if (entry.status !== 'Approved' && entry.status !== 'Executed') return false;
  const open = Number(entry.openQuantityMt);
  return Number.isFinite(open) && open > 0;
}

export interface SettlementFxClosePromptProps {
  orderId: string;
  open: boolean;
  onClose: () => void;
}

// After a Final Settlement invoice is fully paid, prompt to close the order's
// open FX cover (launchpad parity). Self-dismisses when there is nothing to close.
export function SettlementFxClosePrompt({
  orderId,
  open,
  onClose,
}: Readonly<SettlementFxClosePromptProps>) {
  const summary = useOrderHedgingSummary(open ? orderId : undefined);
  const fxHedge = useMemo(() => summary.data?.requests.find(isOpenFxCover) ?? null, [summary.data]);
  useEffect(() => {
    if (open && summary.isSuccess && fxHedge === null) onClose();
  }, [open, summary.isSuccess, fxHedge, onClose]);
  if (!open || fxHedge === null) return null;
  return (
    <FxCloseDialog
      entry={fxHedge}
      open
      onOpenChange={(next) => {
        if (!next) onClose();
      }}
    />
  );
}
